import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import styled from 'styled-components';
import NotificationBell from '../../shared/NotificationBell';

const HeaderContainer = styled.header`
  background-color: black;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15), 
              inset 0 1px 0 rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  padding: 0.75rem 0;
  position: sticky;
  top: 0;
  z-index: 100;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  height: 64px;
  display: flex;
  align-items: center;
  
  &:hover {
    background-color: rgba(15, 23, 42, 0.85);
    box-shadow: 0 6px 30px rgba(0, 0, 0, 0.25), 
                inset 0 1px 0 rgba(255, 255, 255, 0.12);
  }
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1280px;
  width: 100%;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  color: #7EB6FF;
  font-weight: 700;
  font-size: 1.5rem;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  white-space: nowrap;
  letter-spacing: 0.5px;
  
  &:hover {
    transform: translateY(-2px);
    text-shadow: 0 0 15px rgba(126, 182, 255, 0.5);
  }
  
  span {
    color: rgba(255, 255, 255, 0.95);
  }
`;

const LogoImage = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #396AFC 0%, #2948FF 100%);
  border-radius: 12px;
  margin-right: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 1.2rem;
  box-shadow: 0 4px 15px rgba(41, 72, 255, 0.3),
              inset 0 0 0 1px rgba(255, 255, 255, 0.1);
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: -10px;
    left: -10px;
    right: -10px;
    bottom: -10px;
    background: linear-gradient(45deg, #396AFC, transparent, #2948FF, transparent);
    background-size: 400% 400%;
    opacity: 0;
    transition: opacity 0.8s ease;
    z-index: -1;
    animation: none;
  }
  
  &:hover {
    transform: rotate(5deg) scale(1.05);
    box-shadow: 0 8px 25px rgba(41, 72, 255, 0.45),
                inset 0 0 0 1px rgba(255, 255, 255, 0.2);
  }
  
  &:hover:before {
    opacity: 1;
    animation: shimmer 3s linear infinite;
  }
  
  @keyframes shimmer {
    0% { background-position: 0% 0%; }
    50% { background-position: 100% 100%; }
    100% { background-position: 0% 0%; }
  }
`;

const NavLink = styled(Link).withConfig({
  shouldForwardProp: (prop) => prop !== 'active',
})`
  color: ${props => props.active ? '#7EB6FF' : 'rgba(255, 255, 255, 0.8)'};
  text-decoration: none;
  font-weight: 500;
  font-size: 0.95rem;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
  letter-spacing: 0.3px;

  &:hover {
    color: #7EB6FF;
    background-color: rgba(126, 182, 255, 0.1);
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, #7EB6FF, transparent);
    transition: width 0.4s cubic-bezier(0.16, 1, 0.3, 1);
    border-radius: 2px;
  }
  
  &:hover:after {
    width: 70%;
  }
`;

const NavItem = styled.li`
  position: relative;
  display: flex;
  align-items: center;
`;

const GlowDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: linear-gradient(135deg, #7EB6FF, #5E8BFF);
  box-shadow: 0 0 10px rgba(126, 182, 255, 0.7);
  margin-right: 6px;
  position: relative;
  top: -1px;
`;

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    setTimeout(() => {
      navigate('/login');
    }, 10);
  };

  return (
    <HeaderContainer>
      <HeaderContent>
        <Logo to="/">
          <LogoImage>IC</LogoImage>
          <span>Inter<strong>Connect</strong></span>
        </Logo>

        <nav>
          <ul style={{
            display: 'flex',
            listStyle: 'none',
            margin: 0,
            padding: 0,
            gap: '1.5rem',
            alignItems: 'center'
          }}>
            {user ? (
              <>
                <NavItem>
                  <NavLink to="/dashboard" active={isActive('/dashboard')}>
                    <GlowDot />Dashboard
                  </NavLink>
                </NavItem>

                
                {/* Pro Student specific links */}
                {(user.role === 'proStudent') && (
                  <>
                  <NavItem>
                  <NavLink to="/internships" active={isActive('/internships')}>
                    Internships
                  </NavLink>
                  </NavItem>
                  </>
                )}

                {/* Student specific links */}
                {(user.role === 'student') && (
                  <>     
                    <NavItem>
                  <NavLink to="/internships" active={isActive('/internships')}>
                    Internships
                  </NavLink>
                </NavItem>
                  </>
                )}
                           
                
                {/* Company specific links */}
                {user.role === 'company' && (
                  <>
                    
                    
                    <NavItem>
                  <NavLink to="/internships" active={isActive('/internships')}>
                    Internships
                  </NavLink>
                </NavItem>
                  </>
                )}
                

                {/* SCAD Office specific links */}
                {user.role === 'scadOffice' && (
                  <>
                  <NavItem>
                  <NavLink to="/internships" active={isActive('/internships')}>
                    Internships
                  </NavLink>
                </NavItem>
                    
                  </>
                )}

                {/* Show notification bell to all users except faculty */}
                {user.role !== 'faculty' && (
                  <li style={{ position: 'relative' }}>
                    <NotificationBell />
                  </li>
                )}

                {/* Faculty specific links */}
                {user.role === 'faculty' && (
                  <NavItem>
                       <NavItem>
                  <NavLink to="/internships" active={isActive('/internships')}>
                    Internships
                  </NavLink>
                </NavItem>
                  </NavItem>
                )}

                <NavItem>
                  <NavLink as="button" onClick={handleLogout}>
                    Logout
                  </NavLink>
                </NavItem>
              </>
            ) : (
              <>
                <NavItem>
                  <NavLink to="/login">
                    Login
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink to="/company-register">
                    Company Register
                  </NavLink>
                </NavItem>
              </>
            )}
          </ul>
        </nav>
      </HeaderContent>
    </HeaderContainer>
  );
};

export default Header;